import { useState } from 'react';
import { FileIcon, ImageIcon, FileBox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { IAttachmentCategory, IPostAttachment } from './post.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFileDownloader } from '@/lib/s3';
import { filterAttachments } from './post.utils';
import { AttachmentType } from '@/redux/attachment/attachment.d';

export default function DownloadModal({
  attachments,
  postTitle,
}: {
  attachments: IPostAttachment[];
  postTitle: string;
}) {
  const [open, setOpen] = useState(false);
  const { downloadFile } = useFileDownloader();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const getFileIcon = (attachment: IPostAttachment) => {
    switch (attachment.resourceType) {
      case AttachmentType.model:
        return <FileBox className="h-4 w-4 sm:h-6 sm:w-6" />;
      case AttachmentType.image:
        return <ImageIcon className="h-4 w-4 sm:h-6 sm:w-6" />;
      default:
        return <FileIcon className="h-4 w-4 sm:h-6 sm:w-6" />;
    }
  };

  const getFileExtension = (url: string) => {
    const matches = url.match(/\.([0-9a-z]+)(?=[?#]|$)/i);
    return matches ? matches[1] : 'download';
  };

  const handleDownload = async (attachment: IPostAttachment) => {
    setDownloadingId(attachment._id);
    try {
      await downloadFile(attachment._id, attachment.fileName);
    } catch (error) {
      toast.error('Failed to download file, please try again later.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    const postAttachmentsZip = attachments.find((attachment) => {
      if (
        attachment.resourceType === AttachmentType.zip &&
        attachment.category === IAttachmentCategory.postZip
      ) {
        return true;
      }
      return false;
    });
    if (!postAttachmentsZip) {
      toast.error('Download all is currently unavailable', {
        description: 'Please try again later.',
      });
      return;
    }
    setDownloadingId('download_all');
    try {
      await downloadFile(postAttachmentsZip._id, postTitle.replace(/ /g, '_'));
    } catch (error) {
      toast.error('Failed to download files, please try again later.');
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredAttachments = filterAttachments(attachments);
  const totalAttachmentsDownloadCount = filteredAttachments.reduce(
    (count, attachment) => count + (attachment.downloadCount || 0),
    0
  );
  const totalDownloadCount = Math.round(
    totalAttachmentsDownloadCount / filteredAttachments.length
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="bg-secondary rounded-full">
          <Button variant="gradient" size="md">
            Download &nbsp;
            <span className="flex flex-col items-center justify-center h-5 w-5 bg-black bg-opacity-50 text-white rounded-full text-xs">
              {totalDownloadCount}
            </span>
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent className="w-[90vw] max-w-[550px] max-h-[90vh] p-4 flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-2xl font-primary text-left">
            Post Attachments
          </DialogTitle>
        </DialogHeader>
        <div>
          <div className="h-[400px] overflow-y-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px] sm:w-[50px]">Type</TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead className="w-[60px] sm:w-[100px] text-center">
                    Format
                  </TableHead>
                  <TableHead className="w-[80px] sm:w-[100px] text-center">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAttachments.length > 0 ? (
                  filteredAttachments.map((attachment) => (
                    <TableRow key={attachment._id}>
                      <TableCell>{getFileIcon(attachment)}</TableCell>
                      <TableCell>
                        <p className="font-medium leading-none truncate max-w-[100px] sm:max-w-[200px]">
                          {attachment.fileName}
                        </p>
                      </TableCell>
                      <TableCell className="text-center text-xs sm:text-sm">
                        {getFileExtension(attachment.url)}
                      </TableCell>
                      <TableCell>
                        <div className="bg-secondary rounded-full">
                          <Button
                            size="sm"
                            variant="gradient"
                            className="w-full text-xs sm:text-sm py-2 px-3 h-auto"
                            onClick={() => handleDownload(attachment)}
                          >
                            {downloadingId === attachment._id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin text-white" />
                            ) : (
                              'Download'
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No attachments available
                    </TableCell>
                  </TableRow>
                )}
                <div className="pt-12" />
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 border-t pt-4">
            <Button
              size="sm"
              onClick={handleDownloadAll}
              className="w-full"
              disabled={attachments.length === 0}
            >
              {downloadingId === 'download_all' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-white" />
              ) : (
                'Download All'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
